import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import api from '@/lib/api';

const AuthContext = createContext();

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    if (window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }
    try {
      const res = await api.get('/auth/me');
      setUser(res.data);
    } catch {
      setUser(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = async (email, password) => {
    const res = await api.post('/auth/login', { email, password });
    setUser(res.data);
    return res.data;
  };

  const register = async (email, password, name, extra = {}) => {
    // Callers can pass { preferred_language } so the tenant's UI locale is
    // captured on the very first write. Falls back to whatever is in
    // localStorage so emails go out in the language the user signed up in.
    let preferred_language = extra.preferred_language;
    if (!preferred_language) {
      try {
        const stored = window.localStorage.getItem('nestgva.lang');
        if (stored === 'fr' || stored === 'en') preferred_language = stored;
      } catch {}
    }
    const payload = { email, password, name };
    if (extra.phone != null) payload.phone = String(extra.phone).trim();
    if (preferred_language) payload.preferred_language = preferred_language;
    const res = await api.post('/auth/register', payload);
    setUser(res.data);
    return res.data;
  };

  const logout = async () => {
    try { await api.post('/auth/logout'); } catch {}
    setUser(false);
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, register, logout, setUser, checkAuth }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
